import { PanelCard, SelectField, ToggleField } from './panelUi'
import { getSetting, type UserSettings } from './useUserSettings'
import { useWriteSettings } from './useWriteSettings'

/**
 * Library defaults — matches the desktop Library & Metadata section,
 * minus scan/folder/cleanup controls that don't apply in a browser.
 * "Default view mode" is kept because the desktop uses it and the PWA
 * may adopt it later (it's harmless to store).
 */
export function LibrarySettingsPanel({
  settings,
}: {
  settings: UserSettings | null
}) {
  const { updateSetting } = useWriteSettings()

  const sort = getSetting(settings, 'defaultSortOrder')
  const view = getSetting(settings, 'defaultViewMode')
  const showVirtual = getSetting(settings, 'showVirtualMoviesInLibrary')
  const autoMark = getSetting(settings, 'autoMarkAsSeenOnPlay')

  return (
    <PanelCard title="Library">
      <SelectField
        label="Default sort order"
        value={sort ?? 'dateAdded'}
        options={[
          { value: 'dateAdded', label: 'Date added (newest first)' },
          { value: 'userRating', label: 'User rating (highest first)' },
          { value: 'title', label: 'Title (A–Z)' },
          { value: 'year', label: 'Year (newest first)' },
          { value: 'rating', label: 'Rating (highest first)' },
          { value: 'duration', label: 'Duration (longest first)' },
        ]}
        onChange={(v) => updateSetting('defaultSortOrder', v)}
      />

      <SelectField
        label="Default view mode"
        description="How the library tab renders on first open"
        value={view ?? 'grid'}
        options={[
          { value: 'grid', label: 'Grid' },
          { value: 'carousel', label: 'Carousel' },
        ]}
        onChange={(v) => updateSetting('defaultViewMode', v)}
      />

      <ToggleField
        label="Show virtual movies in library"
        description="Include TMDB-only entries you rated but don't own a file for"
        checked={Boolean(showVirtual)}
        onChange={(v) => updateSetting('showVirtualMoviesInLibrary', v)}
      />

      <ToggleField
        label="Auto-mark as seen on play"
        description="Mark movies as seen when you start playing them"
        checked={Boolean(autoMark)}
        onChange={(v) => updateSetting('autoMarkAsSeenOnPlay', v)}
      />
    </PanelCard>
  )
}
